// Persists the access token + minimal user profile under a single localStorage
// key. Versioned namespace ("v1") so a future schema change can migrate cleanly
// without colliding with existing keys (e.g. i18nextLng).

const STORAGE_KEY = "capcam.auth.v1";

export interface StoredUser {
  name: string;
  email: string;
}

export interface UserProfile {
  user_id: number;
  handle: string | null;
  display_name: string | null;
  bio: string | null;
  website_url: string | null;
  avatar_url: string | null;
  banner_url: string | null;
  is_verified: boolean;
  is_private: boolean;
  follower_count: number;
  following_count: number;
  capture_count: number;
  likes_received_count: number;
  saves_received_count: number;
  comments_received_count: number;
  views_received_count: number;
  shares_received_count: number;
  created_at: string;
  updated_at: string;
  last_active_at: string | null;
  is_following: boolean | null;
  is_following_me: boolean | null;
  is_owner: boolean;
}

export interface StoredAuth {
  token: string;
  user: StoredUser;
  profile?: UserProfile | null;
  savedAt: number;
}

const hasWindow = (): boolean => typeof window !== "undefined" && !!window.localStorage;

export const getStoredAuth = (): StoredAuth | null => {
  if (!hasWindow()) return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredAuth;
    if (!parsed || typeof parsed.token !== "string" || !parsed.token) {
      return null;
    }
    return parsed;
  } catch {
    // Corrupt blob — treat as logged-out and clear so we don't keep tripping.
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

export const setStoredAuth = (auth: Omit<StoredAuth, "savedAt">): void => {
  if (!hasWindow()) return;
  const payload: StoredAuth = { ...auth, savedAt: Date.now() };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const clearStoredAuth = (): void => {
  if (!hasWindow()) return;
  window.localStorage.removeItem(STORAGE_KEY);
};

export const AUTH_STORAGE_KEY = STORAGE_KEY;
